import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { lazy, Suspense } from "react";
import { LoadingFallback } from "@/components/ui/LoadingFallback";

// Critical pages (load immediately)
import Index from "./pages/Index";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import { OnboardingGuard } from "./components/onboarding/OnboardingGuard";
import { AppShell } from "./components/layout/AppShell";
import { PublicShell } from "./components/layout/PublicShell";
import { StatusBanner } from "./components/layout/StatusBanner";

// Lazy load dashboards (code splitting for performance)
const SuperAdminDashboard = lazy(() => import("./pages/dashboards/SuperAdminDashboard"));
const StudentDashboard = lazy(() => import("./pages/dashboards/StudentDashboard"));
const KidDashboard = lazy(() => import("./pages/dashboards/KidDashboard"));
const TeacherDashboard = lazy(() => import("./pages/dashboards/TeacherDashboard"));
const ParentDashboard = lazy(() => import("./pages/dashboards/ParentDashboard"));
const AnalyticsDashboard = lazy(() => import("./pages/dashboards/AnalyticsDashboard"));

// Lazy load other pages
const LessonViewer = lazy(() => import("./pages/LessonViewer"));
const QuizTaker = lazy(() => import("./pages/QuizTaker"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const RegisterSchool = lazy(() => import("./pages/RegisterSchool"));
const RegisterIndividual = lazy(() => import("./pages/RegisterIndividual"));
const RegisterParentWizard = lazy(() => import("./pages/RegisterParentWizard"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CertificateVerification = lazy(() => import("./pages/CertificateVerification"));
const ChallengeHistory = lazy(() => import("./pages/ChallengeHistory"));
const Install = lazy(() => import("./pages/Install"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
    },
  },
});

// Dashboard router component that redirects based on user role
const DashboardRouter = () => {
  const { primaryRole, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  switch (primaryRole) {
    case 'super_admin':
      return <Navigate to="/admin" replace />;
    case 'school_admin':
      return <Navigate to="/admin" replace />;
    case 'teacher':
      return <Navigate to="/teacher" replace />;
    case 'student':
      return <Navigate to="/student" replace />;
    case 'parent':
      return <Navigate to="/parent" replace />;
    case 'kid':
      return <Navigate to="/learn" replace />;
    default:
      return <Index />;
  }
};

const AppContent = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <StatusBanner />
      <Routes>
            <Route path="/" element={<DashboardRouter />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<PublicShell><ResetPassword /></PublicShell>} />
            <Route path="/pricing" element={<PublicShell fullBleed><Pricing /></PublicShell>} />
            <Route path="/install" element={<PublicShell><Install /></PublicShell>} />
            <Route path="/certificate/verify/:code" element={<PublicShell><CertificateVerification /></PublicShell>} />
            <Route path="/register/school" element={<PublicShell><RegisterSchool /></PublicShell>} />
            <Route path="/register/individual" element={<PublicShell><RegisterIndividual /></PublicShell>} />
            <Route path="/register/parent" element={<PublicShell><RegisterParentWizard /></PublicShell>} />
            <Route path="/lesson/:lessonId" element={<ProtectedRoute><AppShell fullBleed><LessonViewer /></AppShell></ProtectedRoute>} />
            <Route path="/quiz/:quizId" element={<ProtectedRoute><AppShell fullBleed><QuizTaker /></AppShell></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AppShell><AnalyticsDashboard /></AppShell></ProtectedRoute>} />
            <Route path="/challenges" element={<ProtectedRoute><AppShell><ChallengeHistory /></AppShell></ProtectedRoute>} />
            
            {/* Protected Dashboard Routes */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requiredRole="super_admin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/student" 
              element={
                <ProtectedRoute requiredRole="student">
                  <StudentDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/learn" 
              element={
                <ProtectedRoute requiredRole="kid">
                  <OnboardingGuard>
                    <KidDashboard />
                  </OnboardingGuard>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/teacher" 
              element={
                <ProtectedRoute requiredRole="teacher">
                  <TeacherDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/parent" 
              element={
                <ProtectedRoute requiredRole="parent">
                  <OnboardingGuard>
                    <ParentDashboard />
                  </OnboardingGuard>
                </ProtectedRoute>
              } 
            />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<PublicShell hideFooter><NotFound /></PublicShell>} />
          </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
